import {
  FagmoblerAlgoliaProduct,
  FagmoblerAlgoliaProductVariant,
} from '@fagmobler/types';
import { SearchParameters } from 'algoliasearch-helper';
import {
  InfiniteHitsCache,
  InfiniteHitsCachedHits,
} from 'instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits';
import { getRefinements } from 'instantsearch.js/es/lib/utils';

const SEARCH_RESTORE_POSITION_KEY = 'search_scroll_position';
const SEARCH_INFINITE_SCROLL_CACHE_KEY = 'search_infinite_hits';
const SEARCH_RESTORE_MAX_STEPS = 5;

type CacheState = {
  hits: InfiniteHitsCachedHits<
    FagmoblerAlgoliaProduct | FagmoblerAlgoliaProductVariant
  >;
  key: string;
};

const getLocation = () => {
  return window.location.href;
};

const getRefinementKey = (state: any) => {
  const searchParams = SearchParameters.make(state);
  const refinements = getRefinements({}, searchParams);
  const key = JSON.stringify([state.index, state.optionalFilters, refinements]);

  return key;
};

const hasSessionStorage = () => {
  return typeof window !== 'undefined' && window.sessionStorage;
};

const areAllPropertiesEmptyArrays = (hits: any): boolean => {
  return Object.values(hits).every(
    (value) => Array.isArray(value) && value.length === 0
  );
};

//failing example: [{"location":"https://fagmobler.no/sok?q=hjellegjerde%20medium","key":"[\"try_fagmobler\",null,[]]","hits":{"1":[]}}]
export const customAlgoliaCache: InfiniteHitsCache = {
  read({ state }) {
    if (!hasSessionStorage()) {
      return null;
    }

    const key = getRefinementKey(state);

    const items = sessionStorage.getItem(SEARCH_INFINITE_SCROLL_CACHE_KEY);

    if (!items) {
      return null;
    }

    const parsedItems = JSON.parse(items);

    const item = parsedItems.find(
      (i: any) => i.location === getLocation() && i.key === key
    );

    if (!item) {
      return null;
    }

    const cacheState = item as CacheState;

    if (areAllPropertiesEmptyArrays(cacheState.hits)) {
      return null;
    }

    return cacheState.hits;
  },
  write({ state, hits }) {
    if (!hasSessionStorage()) {
      return null;
    }

    const key = getRefinementKey(state);

    const items = sessionStorage.getItem(SEARCH_INFINITE_SCROLL_CACHE_KEY);

    let parsedItems = items ? JSON.parse(items) : [];

    parsedItems = parsedItems.filter(
      (i: any) => i.location !== getLocation() && i.key !== key
    );

    parsedItems.push({
      location: getLocation(),
      key,
      hits,
    });

    if (parsedItems.length > SEARCH_RESTORE_MAX_STEPS) {
      parsedItems.slice(-1 * SEARCH_RESTORE_MAX_STEPS);
    }

    sessionStorage.setItem(
      SEARCH_INFINITE_SCROLL_CACHE_KEY,
      JSON.stringify(parsedItems)
    );
  },
};

export const scrollPositionCache = {
  read() {
    if (!hasSessionStorage()) {
      return null;
    }
    const positions = sessionStorage.getItem(SEARCH_RESTORE_POSITION_KEY);

    if (!positions) {
      return 0;
    }

    const parsedPositions = JSON.parse(positions);

    const position = parsedPositions.find((p: any) => p.key === getLocation());

    if (!position) {
      return 0;
    }

    return parseInt(position.position);
  },
  write(scrollPosition: number) {
    if (!hasSessionStorage()) {
      return null;
    }
    const positions = sessionStorage.getItem(SEARCH_RESTORE_POSITION_KEY);

    let parsedPositions = positions ? JSON.parse(positions) : [];

    parsedPositions = parsedPositions.filter(
      (p: any) => p.key !== getLocation()
    );

    parsedPositions.push({
      key: getLocation(),
      position: scrollPosition,
    });

    if (parsedPositions.length > SEARCH_RESTORE_MAX_STEPS) {
      parsedPositions = parsedPositions.slice(-1 * SEARCH_RESTORE_MAX_STEPS);
    }

    sessionStorage.setItem(
      SEARCH_RESTORE_POSITION_KEY,
      JSON.stringify(parsedPositions)
    );
  },
  remove() {
    if (!hasSessionStorage()) {
      return null;
    }
    const positions = sessionStorage.getItem(SEARCH_RESTORE_POSITION_KEY);

    if (!positions) {
      return;
    }

    const parsedPositions = JSON.parse(positions);

    const filteredPositions = parsedPositions.filter(
      (p: any) => p.key !== getLocation()
    );

    sessionStorage.setItem(
      SEARCH_RESTORE_POSITION_KEY,
      JSON.stringify(filteredPositions)
    );
  },
};
